import { Controller } from "@hotwired/stimulus"

declare global {
  interface Window {
    Turbo?: { visit: (url: string, options?: { action?: string }) => void }
  }
}

// 深夜帯（終了時刻が閉店時刻に固定される時間帯）か。
// バックエンドの `hour >= LATE_NIGHT_START_HOUR || hour < slot_min_hour` と同じ両側条件。
// 開店時刻未満の時間帯（例: 01:00）は深夜またぎ営業の翌日分なので深夜帯として扱う（issue #331）。
export function isLateNightHour(hour: number, lateNightStartHour: number, slotMinHour: number): boolean {
  return hour >= lateNightStartHour || hour < slotMinHour
}

export default class extends Controller {
  static values = {
    date: String,
    lateNightStartHour: { type: Number, default: 20 },
    // 開店時刻の「時」。これ未満の時間帯は深夜またぎ営業の翌日分であり深夜帯として扱う
    slotMinHour: { type: Number, default: 10 },
    closingTimeLabel: { type: String, default: "翌朝02:00" },
    closingTimeValue: { type: String, default: "02:00" },
    excludeReservationId: { type: String, default: "" }
  }
  declare dateValue: string
  declare lateNightStartHourValue: number
  declare slotMinHourValue: number
  declare closingTimeLabelValue: string
  declare closingTimeValueValue: string
  declare excludeReservationIdValue: string

  changeDate(event: Event): void {
    const input = event.target as HTMLInputElement
    const newDate = input.value
    if (!newDate) return

    const url = new URL(window.location.href)
    url.searchParams.set('date', newDate)
    url.searchParams.delete('start_time')
    url.searchParams.delete('end_time')

    if (window.Turbo) {
      window.Turbo.visit(url.toString(), { action: 'advance' })
    } else {
      window.location.href = url.toString()
    }
  }

  updateEndTimeOptions(event: Event): void {
    const select = event.target as HTMLSelectElement
    const startTime = select.value
    const endTimeFrame = document.getElementById("end_time_frame")

    if (!startTime) {
      // 開始時刻が未選択の場合、カレンダーのハイライトをクリア
      document.dispatchEvent(new CustomEvent('calendar-selection-update', {
        detail: { startTime: '', endTime: '' }
      }))
      return
    }

    // 開始時刻が深夜帯かチェック。
    // バックエンド（reservation_params / change_params）と同じ両側条件にする。
    // 片側条件（hour >= 20 のみ）だと、開店時刻未満の 01:00 のような
    // 営業日の翌日側の開始時刻を深夜帯と判定できず、終了時刻が固定されない（issue #331）。
    const [hour] = startTime.split(':').map(Number)
    if (isLateNightHour(hour, this.lateNightStartHourValue, this.slotMinHourValue)) {
      // 深夜予約の場合、固定の終了時刻を設定（DOM APIでXSS対策）
      if (endTimeFrame) {
        endTimeFrame.textContent = ''

        const fixedDiv = document.createElement('div')
        fixedDiv.className = 'block w-full retro-input bg-gray-50'
        fixedDiv.textContent = `${this.closingTimeLabelValue}（固定）`
        endTimeFrame.appendChild(fixedDiv)

        const hiddenInput = document.createElement('input')
        hiddenInput.type = 'hidden'
        hiddenInput.name = 'reservation[end_time]'
        hiddenInput.value = this.closingTimeValueValue
        hiddenInput.id = 'reservation_end_time'
        endTimeFrame.appendChild(hiddenInput)

        const note = document.createElement('p')
        note.className = 'mt-1 text-sm text-amber-600 font-bold'
        note.textContent = `※ ${this.lateNightStartHourValue}時以降の予約は${this.closingTimeLabelValue}までの固定予約となります`
        endTimeFrame.appendChild(note)
      }
      // 深夜帯の固定終了時刻でカレンダーを更新
      this.dispatchCalendarSelectionUpdate(startTime, this.closingTimeValueValue)
      return
    }

    // 通常時間帯の場合、利用可能な終了時刻オプションを取得
    if (!endTimeFrame) return

    let url = `/reservations/end_time_options?date=${encodeURIComponent(this.dateValue)}&start_time=${encodeURIComponent(startTime)}`
    // 変更画面では編集対象の予約自身を除外し、終了時刻の選択肢が自枠で頭打ちにならないようにする
    if (this.excludeReservationIdValue) {
      url += `&exclude_reservation_id=${encodeURIComponent(this.excludeReservationIdValue)}`
    }

    fetch(url, {
      headers: {
        "Accept": "text/html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then(response => response.text())
      .then(html => {
        // サーバーサイドのパーシャルレンダリング結果を挿入
        // NOTE: /reservations/end_time_options はRailsの信頼済みテンプレートから生成されるHTML
        endTimeFrame.innerHTML = html
        // 終了時刻オプション取得後、開始時刻のみでカレンダーを更新（終了時刻はまだ未選択）
        this.dispatchCalendarSelectionUpdate(startTime, '')
      })
      .catch(error => {
        console.error("終了時刻オプションの取得に失敗:", error)
      })
  }

  // 終了時刻selectの変更時にカレンダーの選択範囲を更新
  updateCalendarSelection(): void {
    const startTimeSelect = document.getElementById("reservation_start_time") as HTMLSelectElement | null
    const endTimeSelect = document.getElementById("reservation_end_time") as HTMLSelectElement | null
    const startTime = startTimeSelect?.value || ''
    const endTime = endTimeSelect?.value || ''
    this.dispatchCalendarSelectionUpdate(startTime, endTime)
  }

  // カレンダーに選択範囲の更新を通知するヘルパー
  private dispatchCalendarSelectionUpdate(startTime: string, endTime: string): void {
    document.dispatchEvent(new CustomEvent('calendar-selection-update', {
      detail: { startTime, endTime }
    }))
  }
}
